import { NotificationSystem } from '@/js/external-js/variableModules/namespaces/NotificationSystem/NotificationSystem.namespace';
import { Timestamp } from 'firebase/firestore';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';

interface ApiResponse<T> {
  statusCode: number;
  data: T;
}

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  persist?: boolean; // Whether to persist to Firebase or just show temporarily
  userID?: string | null; // Target specific user, null for global notifications
  link?: string;
  deletedAt?: Timestamp | null;
}

export class ModernNotificationService {
  private static instance: ModernNotificationService;
  private customerID: string | null = null;
  private currentUserID: string | null = null;

  private constructor() {}

  public static getInstance(): ModernNotificationService {
    if (!ModernNotificationService.instance) {
      ModernNotificationService.instance = new ModernNotificationService();
    }
    return ModernNotificationService.instance;
  }

  public setCustomerID(customerID: string): void {
    this.customerID = customerID;
  }

  public setCurrentUserID(userID: string | null): void {
    this.currentUserID = userID;
  }

  private getIconForType(type: string): string {
    switch (type) {
      case 'success':
        return 'fa fa-duotone fa-solid fa-check-circle';
      case 'error':
        return 'fa fa-duotone fa-solid fa-exclamation-triangle';
      case 'info':
        return 'fa fa-duotone fa-solid fa-info-circle';
      case 'warning':
        return 'fa fa-duotone fa-solid fa-exclamation-circle';
      default:
        return 'fa fa-duotone fa-solid fa-bell';
    }
  }

  private generateNotificationId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Add a persistent notification that will be stored via Thalassa API
   */
  public async addPersistentNotification(options: NotificationOptions): Promise<void> {
    const notification = {
      id: this.generateNotificationId(),
      title: options.title,
      body: options.body,
      icon: options.icon || this.getIconForType(options.type || 'info'),
      timestamp: new Date(),
      read: false,
      userID: options.userID,
      deletedAt: null,
      link: options.link,
    };

    try {
      await deiterateAPI.POST(
        `/v1/notifications`,
        { body: JSON.stringify(notification), backend: 'thalassa', companyID: this.customerID || undefined }
      );
      console.log('Persistent notification added:', notification);
    } catch (error) {
      console.error('Failed to add persistent notification:', error);
      // Fallback to console log if API fails
      console.log(`Notification: ${options.title} - ${options.body}`);
    }
  }

  /**
   * Convenience methods for different notification types
   */
  public async success(title: string, body: string, persist: boolean = true, userID?: string | null, link?: string): Promise<void> {
    if (persist) {
      await this.addPersistentNotification({
        title,
        body,
        type: 'success',
        persist: true,
        userID: userID,
        link: link,
        deletedAt: null,
      });
    } else {
      // For non-persistent, we could integrate with existing toast system or just log
      console.log(`✅ ${title}: ${body}`);
    }
  }

  public async error(title: string, body: string, persist: boolean = true, userID?: string | null, link?: string): Promise<void> {
    if (persist) {
      await this.addPersistentNotification({
        title,
        body,
        type: 'error',
        persist: true,
        userID: userID,
        link: link,
        deletedAt: null,
      });
    } else {
      console.error(`❌ ${title}: ${body}`);
    }
  }

  public async info(title: string, body: string, persist: boolean = true, userID?: string | null, link?: string): Promise<void> {
    if (persist) {
      await this.addPersistentNotification({
        title,
        body,
        type: 'info',
        persist: true,
        userID: userID,
        link: link,
        deletedAt: null,
      });
    } else {
      console.log(`ℹ️ ${title}: ${body}`);
    }
  }

  public async warning(title: string, body: string, persist: boolean = true, userID?: string | null, link?: string): Promise<void> {
    if (persist) {
      await this.addPersistentNotification({
        title,
        body,
        type: 'warning',
        persist: true,
        userID: userID,
        link: link,
        deletedAt: null,
      });
    } else {
      console.warn(`⚠️ ${title}: ${body}`);
    }
  }

  /**
   * Get all notifications for the current customer and user via Thalassa API
   */
  public async getNotifications(limit: number = 10): Promise<NotificationSystem.Notification[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set('limit', limit.toString());
      if (this.currentUserID) {
        queryParams.set('userId', this.currentUserID);
      }
      
      const response = await deiterateAPI.GET<ApiResponse<NotificationSystem.Notification[]>>(
        `/v1/notifications?${queryParams.toString()}`,
        { backend: 'thalassa', companyID: this.customerID || undefined }
      );
      return response.data || [];
    } catch (error) {
      console.error('Failed to get notifications:', error);
      return [];
    }
  }

  /**
   * Mark a notification as read via Thalassa API
   */
  public async markAsRead(notificationId: string, callback?: (notifications: NotificationSystem.Notification[]) => void): Promise<void> {
    try {
      await deiterateAPI.PUT(
        `/v1/notifications/${encodeURIComponent(notificationId)}/read`,
        { backend: 'thalassa', companyID: this.customerID || undefined }
      );
      // Refresh notifications and call callback if provided
      if (callback) {
        const notifications = await this.getNotifications();
        callback(notifications);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }

  /**
   * Mark all notifications as read via Thalassa API
   */
  public async markAllAsRead(callback?: (notifications: NotificationSystem.Notification[]) => void): Promise<void> {
    try {
      await deiterateAPI.PUT(
        `/v1/notifications/read-all`,
        { backend: 'thalassa', companyID: this.customerID || undefined }
      );
      // Refresh notifications and call callback if provided
      if (callback) {
        const notifications = await this.getNotifications();
        callback(notifications);
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }

  /**
   * Clear a notification via Thalassa API
   */
  public async clearNotification(notificationId: string): Promise<void> {
    try {
      if (!this.customerID) {
        console.error('Customer ID not found');
        return;
      }
      console.log('Clearing notification:', notificationId);
      await deiterateAPI.DELETE(
        `/v1/notifications/${encodeURIComponent(notificationId)}`,
        { backend: 'thalassa', companyID: this.customerID }
      );
    } catch (error) {
      console.error('Failed to clear notification:', error);
    }
  }

  /**
   * Listen for new notifications in real-time
   */
  public listenForNotifications(callback: (notifications: NotificationSystem.Notification[]) => void): void {
    NotificationSystem.listenForNewNotifications(this.customerID, callback, this.currentUserID);
  }
}

// Export a singleton instance
export const notificationService = ModernNotificationService.getInstance();

// Export convenience functions for easy use
export const notify = {
  success: (title: string, body: string, persist: boolean = true, userID?: string | null, link?: string) => 
    notificationService.success(title, body, persist, userID, link ? link : undefined),
  error: (title: string, body: string, persist: boolean = true, userID?: string | null, link?: string) => 
    notificationService.error(title, body, persist, userID, link ? link : undefined),
  info: (title: string, body: string, persist: boolean = true, userID?: string | null, link?: string) => 
    notificationService.info(title, body, persist, userID, link ? link : undefined),
  warning: (title: string, body: string, persist: boolean = true, userID?: string | null, link?: string) => 
    notificationService.warning(title, body, persist, userID, link ? link : undefined),
  clearNotification: (notificationId: string) => notificationService.clearNotification(notificationId),
};
